// Swatch library support: user-imported color libraries (ASE / CSV / JSON),
// nearest-match search with CIEDE2000, and sRGB gamut checking.
//
// LEGAL NOTE (clean-room project): this module does NOT bundle, embed, or
// generate any proprietary color data (no Pantone, RAL, HKS, ...). Libraries
// are always imported by the user from their own files. Nothing here was
// derived from any vendor's code or data files; the ASE (Adobe Swatch
// Exchange) *file structure* is implemented from its publicly documented
// layout. See: "ASE file format" public documentation.
//
// Assumptions (documented per project rules):
//  - ASE 'RGB ' values are 0..1 floats; 'Lab ' values are L 0..100, a/b
//    roughly -128..128 (per the published ASE layout).
//  - ASE 'CMYK' entries are converted with the naive formula
//    R = (1-C)(1-K) etc. This is profile-independent and therefore only an
//    approximation; the swatch is tagged model 'CMYK (naive)'.
//  - ASE 'Gray' entries are converted R = G = B = value.
//  - CSV: delimiter auto-detected (, ; tab). Header row detected by known
//    column names (name/code/hex/r/g/b/L/a/b). Without a header the layout
//    is assumed "name,hex" (2 cols) or "name,R,G,B" (4 cols, 0..255).
//  - JSON: accepts a bare array or { swatches: [...] }; per entry:
//    { name?, code?, hex } | { rgb: [r,g,b] } | { lab: {L,a,b} | [L,a,b] }.
//    RGB arrays with any component > 1 are treated as 0..255, otherwise 0..1.

import type { LabColor, SRGB } from '../types';
import { srgbToLab, labToSrgb } from '../color/spaces';
import { deltaE2000 } from '../color/deltaE';

export interface Swatch {
  name: string;
  code?: string;
  lab: LabColor;            // canonical color
  model: string;            // source model tag, e.g. 'hex', 'RGB', 'Lab', 'CMYK (naive)'
}

export interface NearestSwatchResult {
  index: number;
  swatch: Swatch;
  deltaE: number;           // CIEDE2000
}

/* ------------------------------------------------------------------ */
/* Color helpers                                                      */
/* ------------------------------------------------------------------ */

/** Parse #rgb / #rrggbb (case-insensitive). Returns null when invalid. */
export function hexToLab(hex: string): LabColor | null {
  const srgb = hexToSrgb(hex);
  return srgb ? srgbToLab(srgb) : null;
}

export function hexToSrgb(hex: string): SRGB | null {
  let h = hex.trim();
  if (h.startsWith('#')) h = h.slice(1);
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

/** Lab -> #rrggbb (clamped to sRGB gamut; use isInSrgbGamut to detect clipping). */
export function labToHex(lab: LabColor): string {
  const s = labToSrgb(lab);
  const to2 = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${to2(s.r)}${to2(s.g)}${to2(s.b)}`;
}

/**
 * Unclamped Lab -> linear RGB (no gamut clamp). Used for gamut testing.
 * Same D65 matrix as spaces.ts labToRgbLinear but without clamp01.
 */
export function labToRgbLinearUnclamped(lab: LabColor): { r: number; g: number; b: number } {
  const fy = (lab.L + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;
  const delta = 6 / 29;
  const xr = fx > delta ? fx * fx * fx : 3 * delta * delta * (fx - 4 / 29);
  const yr = lab.L > 8 ? fy * fy * fy : 3 * delta * delta * (fy - 4 / 29);
  const zr = fz > delta ? fz * fz * fz : 3 * delta * delta * (fz - 4 / 29);
  const X = xr * 0.95047;
  const Y = yr * 1.0;
  const Z = zr * 1.08883;
  return {
    r: X * 3.2404542 + Y * -1.5371385 + Z * -0.4985314,
    g: X * -0.9692660 + Y * 1.8760108 + Z * 0.0415560,
    b: X * 0.0556434 + Y * -0.2040259 + Z * 1.0572252,
  };
}

const GAMUT_EPS = 1e-4;

/** True when the Lab color converts to linear RGB within [0,1] (sRGB). */
export function isInSrgbGamut(lab: LabColor): boolean {
  const c = labToRgbLinearUnclamped(lab);
  return (
    c.r >= -GAMUT_EPS && c.r <= 1 + GAMUT_EPS &&
    c.g >= -GAMUT_EPS && c.g <= 1 + GAMUT_EPS &&
    c.b >= -GAMUT_EPS && c.b <= 1 + GAMUT_EPS
  );
}

/**
 * Print-relevance warning for a color (or null when nothing notable).
 *  - out of sRGB gamut: the Lab color cannot be represented in sRGB at all
 *    (screen preview and RGB-based RIPs will clip it).
 *  - high chroma: beyond typical textile ink gamuts on many substrates.
 * Thresholds are engineering heuristics, not a paper/ink profile.
 */
export function gamutWarning(lab: LabColor): string | null {
  const warnings: string[] = [];
  if (!isInSrgbGamut(lab)) {
    warnings.push('Outside sRGB gamut — preview will clip this color.');
  }
  const chroma = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  if (chroma > 90) {
    warnings.push(`Very high chroma (C* = ${chroma.toFixed(0)}) — may be outside typical ink gamut.`);
  }
  if (lab.L < 2 || lab.L > 98) {
    warnings.push('Extreme lightness — check substrate limits.');
  }
  return warnings.length ? warnings.join(' ') : null;
}

/* ------------------------------------------------------------------ */
/* Search                                                             */
/* ------------------------------------------------------------------ */

/** Nearest library swatch to `lab` by CIEDE2000 (ΔE00). */
export function findNearestSwatch(
  swatches: Swatch[],
  lab: LabColor
): NearestSwatchResult | null {
  if (!swatches.length) return null;
  let best = 0;
  let bestDe = Infinity;
  for (let i = 0; i < swatches.length; i++) {
    const d = deltaE2000(swatches[i].lab, lab);
    if (d < bestDe) {
      bestDe = d;
      best = i;
    }
  }
  return { index: best, swatch: swatches[best], deltaE: bestDe };
}

/** Case-insensitive substring search over name and code. */
export function searchSwatches(swatches: Swatch[], query: string): Swatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return swatches;
  return swatches.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      (s.code ? s.code.toLowerCase().includes(q) : false)
  );
}

/* ------------------------------------------------------------------ */
/* JSON import                                                        */
/* ------------------------------------------------------------------ */

function normalizeJsonEntry(raw: any, index: number): Swatch | null {
  if (raw == null || typeof raw !== 'object') return null;
  const name =
    (typeof raw.name === 'string' && raw.name) ||
    (typeof raw.colorName === 'string' && raw.colorName) ||
    (typeof raw.title === 'string' && raw.title) ||
    (typeof raw.code === 'string' && raw.code) ||
    (typeof raw.id === 'string' && raw.id) ||
    `Swatch ${index + 1}`;
  const code =
    (typeof raw.code === 'string' && raw.code) ||
    (typeof raw.id === 'string' && raw.id) ||
    undefined;

  // hex wins if present
  if (typeof raw.hex === 'string') {
    const lab = hexToLab(raw.hex);
    if (lab) return { name, code, lab, model: 'hex' };
    return null;
  }
  if (typeof raw.value === 'string' && raw.value.startsWith('#')) {
    const lab = hexToLab(raw.value);
    if (lab) return { name, code, lab, model: 'hex' };
    return null;
  }

  if (Array.isArray(raw.lab) && raw.lab.length === 3) {
    const [L, a, b] = raw.lab.map(Number);
    if ([L, a, b].every(Number.isFinite)) {
      return { name, code, lab: { L, a, b }, model: 'Lab' };
    }
    return null;
  }
  if (raw.lab && typeof raw.lab === 'object') {
    const { L, a, b } = raw.lab;
    if ([L, a, b].every((v: any) => Number.isFinite(Number(v)))) {
      return { name, code, lab: { L: +L, a: +a, b: +b }, model: 'Lab' };
    }
    return null;
  }

  if (Array.isArray(raw.rgb) && raw.rgb.length === 3) {
    const comps = raw.rgb.map(Number);
    if (!comps.every(Number.isFinite)) return null;
    // Scale assumption: any component > 1 => 0..255, otherwise 0..1.
    const scale = comps.some((v: number) => v > 1) ? 255 : 1;
    const srgb: SRGB = {
      r: comps[0] / scale,
      g: comps[1] / scale,
      b: comps[2] / scale,
    };
    return { name, code, lab: srgbToLab(srgb), model: 'RGB' };
  }

  return null;
}

export function parseSwatchJson(text: string): Swatch[] {
  const data = JSON.parse(text);
  const arr = Array.isArray(data)
    ? data
    : Array.isArray(data?.swatches)
      ? data.swatches
      : Array.isArray(data?.colors)
        ? data.colors
        : null;
  if (!arr) throw new Error('JSON: expected an array (or { swatches: [...] })');
  const out: Swatch[] = [];
  arr.forEach((raw: any, i: number) => {
    const s = normalizeJsonEntry(raw, i);
    if (s) out.push(s);
  });
  if (!out.length) throw new Error('JSON: no recognizable swatch entries');
  return out;
}

/* ------------------------------------------------------------------ */
/* CSV import                                                         */
/* ------------------------------------------------------------------ */

function detectDelimiter(line: string): string {
  const counts: Record<string, number> = {
    ',': (line.match(/,/g) || []).length,
    ';': (line.match(/;/g) || []).length,
    '\t': (line.match(/\t/g) || []).length,
  };
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

const HEADER_ALIASES: Record<string, string> = {
  name: 'name', color: 'name', colour: 'name', colourname: 'name',
  code: 'code', id: 'code', ref: 'code', reference: 'code',
  hex: 'hex', hexvalue: 'hex', value: 'hex',
  r: 'r', red: 'r',
  g: 'g', green: 'g',
  b: 'b', blue: 'b',
  l: 'L', lightness: 'L', lstar: 'L',
  a: 'a', astar: 'a',
};

export function parseSwatchCsv(text: string): Swatch[] {
  const lines = text
    .split(/\r\n|\n|\r/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (!lines.length) throw new Error('CSV: empty file');

  const delim = detectDelimiter(lines[0]);
  const split = (l: string) => l.split(delim).map((c) => c.trim().replace(/^"|"$/g, ''));

  const first = split(lines[0]);
  const firstLower = first.map((c) => c.toLowerCase().replace(/[\s_]/g, ''));
  // Header rules (documented assumption): no cell may look like hex data
  // (#...), cells must be word-like, and at least 2 columns must be known
  // aliases (or every column must be one). This keeps "Red,#FF0000" rows on
  // the positional path instead of being mistaken for a header.
  const aliasHits = firstLower.filter((c) => c in HEADER_ALIASES).length;
  const wordLike = firstLower.every(
    (c) => c in HEADER_ALIASES || /^[a-z][a-z\s.]*$/.test(c)
  );
  const looksLikeHeader =
    !first.some((c) => c.startsWith('#')) &&
    wordLike &&
    (aliasHits >= 2 || aliasHits === first.length);

  let cols: Record<string, number> = {};
  let start = 0;
  if (looksLikeHeader) {
    firstLower.forEach((c, i) => {
      const key = HEADER_ALIASES[c];
      if (key && !(key in cols)) cols[key] = i;
    });
    start = 1;
  }

  const out: Swatch[] = [];
  for (let li = start; li < lines.length; li++) {
    const cells = split(lines[li]);
    if (!cells.length) continue;

    // With a header but no name column, data cells are color values -> synthesize names.
    const nameIdx = looksLikeHeader && cols.name === undefined ? -1 : (cols.name ?? 0);
    const name = nameIdx >= 0 && cells[nameIdx] ? cells[nameIdx] : `Swatch ${out.length + 1}`;
    const code = cols.code !== undefined ? cells[cols.code] || undefined : undefined;

    let lab: LabColor | null = null;
    let model = '';

    if (cols.hex !== undefined && cells[cols.hex]) {
      lab = hexToLab(cells[cols.hex]);
      model = 'hex';
    } else if (
      cols.r !== undefined && cols.g !== undefined && cols.b !== undefined &&
      cells[cols.r] !== undefined && cells[cols.g] !== undefined && cells[cols.b] !== undefined
    ) {
      const r = Number(cells[cols.r]);
      const g = Number(cells[cols.g]);
      const b = Number(cells[cols.b]);
      if ([r, g, b].every(Number.isFinite)) {
        // r/g/b columns: >1 means 0..255, else 0..1 (documented assumption)
        const scale = r > 1 || g > 1 || b > 1 ? 255 : 1;
        lab = srgbToLab({ r: r / scale, g: g / scale, b: b / scale });
        model = 'RGB';
      }
    } else if (
      cols.L !== undefined && cols.a !== undefined && cols.b !== undefined &&
      cells[cols.L] !== undefined && cells[cols.a] !== undefined && cells[cols.b] !== undefined
    ) {
      const L = Number(cells[cols.L]);
      const a = Number(cells[cols.a]);
      const bV = Number(cells[cols.b]);
      if ([L, a, bV].every(Number.isFinite)) {
        lab = { L, a, b: bV };
        model = 'Lab';
      }
    } else if (!looksLikeHeader) {
      // Positional fallback without header (documented assumptions):
      //   2 cols -> name,hex ; 4 cols -> name,R,G,B (0..255)
      if (cells.length === 2 && cells[1]) {
        lab = hexToLab(cells[1]);
        model = 'hex';
      } else if (cells.length >= 4) {
        const r = Number(cells[1]);
        const g = Number(cells[2]);
        const b = Number(cells[3]);
        if ([r, g, b].every(Number.isFinite)) {
          // Positional 4-col layout is documented as 0..255 (no header to
          // disambiguate, so this is a fixed assumption).
          lab = srgbToLab({ r: r / 255, g: g / 255, b: b / 255 });
          model = 'RGB';
        }
      }
    }

    if (lab) out.push({ name, code, lab, model });
  }

  if (!out.length) throw new Error('CSV: no recognizable swatch rows');
  return out;
}

/* ------------------------------------------------------------------ */
/* ASE import (Adobe Swatch Exchange, public binary layout)           */
/* ------------------------------------------------------------------ */

export function parseSwatchAse(buffer: ArrayBuffer): Swatch[] {
  const view = new DataView(buffer);
  if (buffer.byteLength < 12) throw new Error('ASE: file too small');

  // 'ASEF'
  const sig = String.fromCharCode(
    view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)
  );
  if (sig !== 'ASEF') throw new Error('ASE: bad signature (expected ASEF)');

  const blockCount = view.getUint32(8, false); // big-endian
  let offset = 12;
  const out: Swatch[] = [];

  for (let i = 0; i < blockCount && offset + 6 <= buffer.byteLength; i++) {
    const blockType = view.getUint16(offset, false);
    const blockLength = view.getUint32(offset + 2, false);
    const blockStart = offset + 6;
    const blockEnd = Math.min(blockStart + blockLength, buffer.byteLength);

    if (blockType === 0x0002) {
      // Color entry
      try {
        let p = blockStart;
        const nameLen = view.getUint16(p, false); // UTF-16 char count incl. NUL
        p += 2;
        let name = '';
        for (let c = 0; c < nameLen; c++) {
          const code = view.getUint16(p, false);
          p += 2;
          if (code !== 0) name += String.fromCharCode(code);
        }
        // Color model: 4 chars
        const model = String.fromCharCode(
          view.getUint8(p), view.getUint8(p + 1),
          view.getUint8(p + 2), view.getUint8(p + 3)
        );
        p += 4;
        const v0 = view.getFloat32(p, false);
        const v1 = view.getFloat32(p + 4, false);
        const v2 = view.getFloat32(p + 8, false);
        const v3 = view.getFloat32(p + 12, false);

        let lab: LabColor | null = null;
        let tag = model.trim();
        if (model === 'RGB ') {
          lab = srgbToLab({ r: v0, g: v1, b: v2 });
        } else if (model === 'Lab ') {
          lab = { L: v0, a: v1, b: v2 };
          tag = 'Lab';
        } else if (model === 'CMYK') {
          // Naive, profile-independent approximation (documented assumption)
          const c = v0, m = v1, y = v2, k = v3;
          lab = srgbToLab({
            r: (1 - c) * (1 - k),
            g: (1 - m) * (1 - k),
            b: (1 - y) * (1 - k),
          });
          tag = 'CMYK (naive)';
        } else if (model === 'Gray') {
          lab = srgbToLab({ r: v0, g: v0, b: v0 });
        }

        if (lab && name) {
          out.push({ name, lab, model: tag });
        }
      } catch {
        // skip malformed entry
      }
    }
    // else: skip group markers (0x0000/0x0001/0x0003) and unknown blocks

    offset = blockEnd;
  }

  if (!out.length) throw new Error('ASE: no color entries found');
  return out;
}

/* ------------------------------------------------------------------ */
/* Dispatch by file name                                              */
/* ------------------------------------------------------------------ */

export function parseSwatchFile(fileName: string, data: ArrayBuffer | string): Swatch[] {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.ase')) {
    if (typeof data === 'string') {
      throw new Error('ASE requires binary input');
    }
    return parseSwatchAse(data);
  }
  const text = typeof data === 'string' ? data : new TextDecoder().decode(data);
  if (lower.endsWith('.json')) return parseSwatchJson(text);
  if (lower.endsWith('.csv') || lower.endsWith('.txt')) return parseSwatchCsv(text);
  // Unknown extension: sniff
  const t = text.trim();
  if (t.startsWith('[') || t.startsWith('{')) return parseSwatchJson(t);
  return parseSwatchCsv(t);
}
